import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  styled,
  useTheme,
  useMediaQuery,
  Tab,
  Tabs,
} from "@mui/material";
import * as XLSX from "xlsx";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import Swal from "sweetalert2";
import { toast, ToastContainer } from "react-toastify";
import { useDispatch } from "react-redux";
import { getTeachersThunk, saveTeacherThunk } from "./TeachersDataUplaodThunk"; // Import your thunks

const DropZone = styled(Paper)(({ theme, isdragover }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(4),
  border: `2px dashed ${
    isdragover === "true" ? theme.palette.primary.main : "#ddd"
  }`,
  borderRadius: theme.shape.borderRadius,
  backgroundColor:
    isdragover === "true" ? "rgba(0, 123, 255, 0.05)" : "#f8f9fa",
  cursor: "pointer",
  transition: "all 0.3s ease",
  minHeight: "200px",
  height: "100%",
  "&:hover": {
    borderColor: theme.palette.primary.main,
    backgroundColor: "rgba(0, 123, 255, 0.05)",
  },
}));

const PreviewTable = styled(TableContainer)(({ theme }) => ({
  marginTop: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[2],
  overflowX: "auto",
  "& .MuiTableCell-head": {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontWeight: "bold",
  },
  "& .MuiTableRow-root:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(0.5),
  textTransform: "none",
  fontWeight: "bold",
  padding: theme.spacing(1, 2),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(1.5, 3),
  },
}));

const TeacherDataUpload = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [file, setFile] = useState(null);
  const [teacherData, setTeacherData] = useState([]);
  const [fetchedTeachers, setFetchedTeachers] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [fileName, setFileName] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchTermFetched, setSearchTermFetched] = useState("");
  const [page, setPage] = useState(0);
  const [pageFetched, setPageFetched] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const rowsPerPage = 10;

  const dispatch = useDispatch();
  const fileInputRef = useRef(null);

  // Required columns based on your sample data
  const requiredColumns = [
    "teacher_id",
    "teacher_name",
    "specializations",
    "grade",
    "phone_number",
    "email_id",
    "udise_code",
    "school",
    "cluster",
    "block",
    "district",
    "state",
  ];

  const requiredColumnsAfterAPi = [
    "teacherId",
    "teacherName",
    "specializations",
    "grade",
    "phoneNumber",
    "emailId",
    "udiseCode",
    "school",
    "cluster",
    "block",
    "district",
    "state",
  ];

  const displayColumns = [
    { key: "teacherId", label: "Teacher ID" },
    { key: "teacherName", label: "Teacher Name" },
    { key: "specializations", label: "Specializations" },
    { key: "grade", label: "Grade" },
    { key: "phoneNumber", label: "Phone Number" },
    { key: "emailId", label: "Email" },
    { key: "udiseCode", label: "UDISE Code" },
    { key: "school", label: "School" },
    { key: "cluster", label: "Cluster" },
    { key: "block", label: "Block" },
    { key: "district", label: "District" },
    { key: "state", label: "State" },
  ];

  // Grade options
  const gradeOptions = [
    { value: "primary", label: "Primary" },
    { value: "secondary", label: "Secondary" },
    { value: "higher_secondary", label: "Higher Secondary" },
  ];

  const fetchTeachers = () => {
    setFetchLoading(true);
    dispatch(getTeachersThunk())
      .then((response) => {
        setFetchedTeachers(response.payload || []);
        // toast.success(`Fetched ${response.payload.length} teachers`);
      })
      .catch((error) => {
        toast.error(`Failed to fetch teachers: ${error.message}`);
      })
      .finally(() => {
        setFetchLoading(false);
      });
  };

  useEffect(() => {
    fetchTeachers();
  }, []);

  // Handle file selection
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    if (
      selectedFile.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      selectedFile.name.endsWith(".xlsx") ||
      selectedFile.name.endsWith(".xls")
    ) {
      setFile(selectedFile);
      setFileName(selectedFile.name);
      processExcelFile(selectedFile);
    } else {
      toast.error("Please select a valid Excel file (.xlsx or .xls)");
    }
  };

  // Process the Excel file
  const processExcelFile = (file) => {
    setLoading(true);
    setProgress(0);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

        // Simulate progress
        const interval = setInterval(() => {
          setProgress((prev) => {
            if (prev >= 95) {
              clearInterval(interval);
              return 100;
            }
            return prev + 5;
          });
        }, 100);

        // Validate data
        if (jsonData.length === 0) {
          toast.error("The selected file contains no data");
          setLoading(false);
          return;
        }

        // Check if data is nested under "teachers" key
        let teachersArray = jsonData;
        if (jsonData[0].teachers) {
          teachersArray = jsonData[0].teachers;
        } else if (Array.isArray(jsonData)) {
          teachersArray = jsonData;
        }

        if (teachersArray.length === 0) {
          toast.error("No teacher data found in the file");
          setLoading(false);
          return;
        }

        // Check required columns
        const missingColumns = requiredColumns.filter(
          (col) => !Object.keys(teachersArray[0]).includes(col)
        );

        if (missingColumns.length > 0) {
          toast.error(`Missing required columns: ${missingColumns.join(", ")}`);
          setLoading(false);
          return;
        }

        // Process data
        setTimeout(() => {
          const processedData = teachersArray.map((teacher, index) => {
            // Process specializations - convert to array
            let specializations = [];
            if (teacher.specializations) {
              // Split by either comma or newline, trim whitespace, and filter out empty strings
              specializations = teacher.specializations
                .toString()
                .split(/[,\n]/)
                .map((s) => s.trim())
                .filter((s) => s.length > 0);
            }

            // Validate each field
            const errors = [];

            let teacherId = teacher.teacher_id?.toString().trim();
            if (!teacherId) {
              teacherId = null;
            }

            // Process phone number - convert to string and prepend 91 if needed
            let phoneStr = teacher.phone_number?.toString()?.trim() || "";
            phoneStr = phoneStr.replace(/\D/g, "");
            if (phoneStr.length === 10) {
              phoneStr = `91${phoneStr}`;
            }
            if (!phoneStr) {
              errors.push("Phone number is required");
            } else if (!/^91\d{10}$/.test(phoneStr)) {
              errors.push(
                "Phone number must be 10 digits (without country code)"
              );
            }

            // Validate email
            if (
              teacher.email_id &&
              !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(teacher.email_id)
            ) {
              errors.push("Invalid email format");
            }

            // Validate UDISE code
            if (
              teacher.udise_code &&
              teacher.udise_code.toString().length !== 7
            ) {
              errors.push("UDISE code must be 7 digits");
            }

            // Validate specializations
            if (specializations.length === 0) {
              errors.push("At least one specialization is required");
            }

            return {
              ...teacher,
              teacher_id: teacherId,
              id: index + 1, // Add temporary ID
              phone_number: phoneStr, // Store as string with country code
              specializations: specializations, // Store as array
              errors: errors.length > 0 ? errors : null,
              isValid: errors.length === 0,
            };
          });

          setTeacherData(processedData);
          setLoading(false);
          setProgress(100);
          clearInterval(interval);

          // Show validation summary
          const invalidCount = processedData.filter((t) => !t.isValid).length;
          if (invalidCount > 0) {
            toast.warning(
              `${invalidCount} records have validation issues. Please review before uploading.`
            );
          }
        }, 1000);
      } catch (error) {
        console.error("Error processing file:", error);
        toast.error("Error processing the Excel file");
        setLoading(false);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // Format phone numbers
  const formatPhoneNumber = (phone) => {
    if (!phone) return "";
    const cleaned = phone.toString().replace(/\D/g, "");
    return cleaned.length === 10 ? `91${cleaned}` : cleaned;
  };

  // Handle drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files.length) {
      const droppedFile = e.dataTransfer.files[0];
      setFile(droppedFile);
      setFileName(droppedFile.name);
      processExcelFile(droppedFile);
    }
  };

  // Handle file upload
  const handleUpload = () => {
    if (teacherData.length === 0) {
      toast.warning("No data to upload");
      return;
    }

    // Check for invalid records
    const invalidRecords = teacherData.filter((t) => !t.isValid);
    if (invalidRecords.length > 0) {
      Swal.fire({
        title: "Validation Issues",
        text: `${invalidRecords.length} records have validation errors. Do you want to proceed anyway?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, upload anyway",
        cancelButtonText: "No, fix them first",
      }).then((result) => {
        if (result.isConfirmed) {
          performUpload();
        }
      });
    } else {
      performUpload();
    }
  };

  const performUpload = () => {
    // Transform data to match API expectations
    const dataToSubmit = {
      teachers: teacherData.map((teacher) => {
        // Map Excel column names to API expected field names
        return {
          teacherId: teacher.teacher_id,
          teacherName: teacher.teacher_name,
          specializations: Array.isArray(teacher.specializations)
            ? teacher.specializations
            : [teacher.specializations],
          grade: teacher.grade,
          phoneNumber: teacher.phone_number,
          emailId: teacher.email_id,
          udiseCode: teacher.udise_code,
          school: teacher.school,
          cluster: teacher.cluster,
          block: teacher.block,
          district: teacher.district,
          state: teacher.state,
        };
      }),
    };

    setUploading(true);

    // Log the data being sent for debugging
    console.log("Data being submitted:", JSON.stringify(dataToSubmit, null, 2));

    dispatch(saveTeacherThunk(dataToSubmit))
      .then((response) => {
        if (response.error) {
          throw new Error(response.error.message || "Upload failed");
        }

        Swal.fire({
          title: "Success!",
          text: `${teacherData.length} teacher records uploaded successfully`,
          icon: "success",
        });

        // Clear form
        setTeacherData([]);
        setFileName("");
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }

        // Refresh teachers list
        return dispatch(getTeachersThunk());
      })
      .then(() => {
        toast.success("Data refreshed successfully");
      })
      .catch((error) => {
        console.error("Upload error:", error);
        toast.error(`Upload failed: ${error.message}`);

        // More detailed error handling
        if (error.response) {
          console.error("Server response:", error.response.data);
          toast.error(`Server error: ${JSON.stringify(error.response.data)}`);
        }
      })
      .finally(() => {
        setUploading(false);
      });
  };
  // Handle reset
  const handleReset = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "This will clear all the current data",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, clear it!",
    }).then((result) => {
      if (result.isConfirmed) {
        setTeacherData([]);
        setFileName("");
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    });
  };

  // Filter data based on search term
  const filteredData = teacherData.filter((teacher) =>
    Object.values(teacher).some(
      (value) =>
        value &&
        value.toString().toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const renderSpecializations = (specializations) => {
    if (Array.isArray(specializations)) {
      return specializations.join(", ");
    }
    return specializations;
  };

  const renderPhoneNumberError = (errors) => {
    const phoneError = errors?.find((e) => e.includes("Phone number"));
    if (phoneError) {
      return "Must be 10 digits (without country code)";
    }
    return errors?.join(", ");
  };

  const filteredFetchedData = fetchedTeachers.filter((teacher) =>
    Object.values(teacher).some(
      (value) =>
        value &&
        value.toString().toLowerCase().includes(searchTermFetched.toLowerCase())
    )
  );

  // const filteredFetchedData =
  //   fetchedTeachers?.filter((teacher) =>
  //     Object.values(teacher).some(
  //       (value) =>
  //         value &&
  //         value
  //           .toString()
  //           .toLowerCase()
  //           .includes(searchTermFetched.toLowerCase())
  //     )
  //   ) || [];

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleDownloadExcel = () => {
    if (fetchedTeachers.length === 0) {
      toast.warning("No data to download");
      return;
    }

    // Create a new workbook
    const wb = XLSX.utils.book_new();

    // Convert data to worksheet
    const ws = XLSX.utils.json_to_sheet(fetchedTeachers);

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, "Teachers");

    // Generate file and download
    XLSX.writeFile(wb, "teachers_data.xlsx");
  };

  return (
    <Box sx={{ p: isMobile ? 1 : 3 }}>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: "bold", mb: 3 }}>
        Teacher's Data Management
      </Typography>

      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3 }}>
        <Tab label="Upload Data" />
        <Tab label="View Teachers" />
      </Tabs>

      {activeTab === 0 ? (
        // Upload Data Tab
        <Grid container spacing={3}>
          {/* Upload Section */}
          <Grid item xs={12} md={6}>
            <DropZone
              isdragover={isDragOver.toString()}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              {loading ? (
                <Box sx={{ textAlign: "center" }}>
                  <CircularProgress variant="determinate" value={progress} />
                  <Typography variant="body1" sx={{ mt: 2 }}>
                    Processing... {progress}%
                  </Typography>
                </Box>
              ) : (
                <>
                  <CloudUploadIcon
                    sx={{ fontSize: 48, color: "primary.main", mb: 2 }}
                  />
                  <Typography variant="h6" sx={{ mb: 1, textAlign: "center" }}>
                    {fileName || "Drag & Drop Excel File"}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2, textAlign: "center" }}
                  >
                    {fileName
                      ? "Click to change file"
                      : "or click to browse files"}
                  </Typography>
                  <Button
                    variant="contained"
                    component="label"
                    startIcon={<UploadFileIcon />}
                    onClick={(e) => e.stopPropagation()}
                  >
                    Select File
                    <input
                      type="file"
                      hidden
                      ref={fileInputRef}
                      onChange={handleFileChange}
                      accept=".xlsx, .xls"
                    />
                  </Button>
                </>
              )}
            </DropZone>
          </Grid>

          {/* Stats Section */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: isMobile ? 2 : 3, height: "107%" }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
                Upload Summary
              </Typography>

              {teacherData.length > 0 ? (
                <>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>File Name:</strong> {fileName}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Total Records:</strong> {teacherData.length}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      mb: 3,
                      color: teacherData.some((t) => !t.isValid)
                        ? "error.main"
                        : "success.main",
                    }}
                  >
                    <strong>Valid Records:</strong>{" "}
                    {teacherData.filter((t) => t.isValid).length} /{" "}
                    {teacherData.length}
                  </Typography>

                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    <ActionButton
                      variant="contained"
                      color="primary"
                      onClick={handleUpload}
                      disabled={uploading || teacherData.length === 0}
                      startIcon={
                        uploading ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : null
                      }
                      fullWidth={isMobile}
                    >
                      {uploading ? "Uploading..." : "Upload Data"}
                    </ActionButton>

                    <ActionButton
                      variant="outlined"
                      color="error"
                      onClick={handleReset}
                      startIcon={<DeleteIcon />}
                      fullWidth={isMobile}
                    >
                      Clear Data
                    </ActionButton>
                  </Box>
                </>
              ) : (
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ textAlign: "center", mt: 4 }}
                >
                  No data available. Please upload an Excel file to preview.
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
      ) : (
        // View Teachers Tab
        <Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
              gap: 2,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              Teacher Database ({fetchedTeachers.length} records)
            </Typography>

            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                variant="outlined"
                size="small"
                placeholder="Search teachers..."
                value={searchTermFetched}
                onChange={(e) => setSearchTermFetched(e.target.value)}
                sx={{ width: isMobile ? "100%" : 250 }}
                InputProps={{
                  startAdornment: (
                    <SearchIcon sx={{ color: "action.active", mr: 1 }} />
                  ),
                }}
              />

              <Button
                variant="contained"
                onClick={fetchTeachers}
                disabled={fetchLoading}
                startIcon={
                  fetchLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <RefreshIcon />
                  )
                }
              >
                Refresh
              </Button>

              <Button
                variant="outlined"
                onClick={handleDownloadExcel}
                disabled={fetchedTeachers.length === 0}
              >
                Export Excel
              </Button>
            </Box>
          </Box>

          {fetchLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <CircularProgress />
            </Box>
          ) : fetchedTeachers.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="body1" color="text.secondary">
                No teacher data available, please upload data first.
              </Typography>
            </Paper>
          ) : (
            <>
              <PreviewTable component={Paper}>
                <Table stickyHeader size={isMobile ? "small" : "medium"}>
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      {requiredColumns.map((key) => (
                        <TableCell key={key}>
                          {key.replace(/_/g, " ").toUpperCase()}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {filteredFetchedData
                      .slice(
                        pageFetched * rowsPerPage,
                        pageFetched * rowsPerPage + rowsPerPage
                      )
                      .map((teacher, index) => (
                        <TableRow key={teacher.teacherId || index}>
                          {" "}
                          {console.log(teacher)}
                          <TableCell>{index + 1}</TableCell>
                          {requiredColumnsAfterAPi.map((key) => (
                            <TableCell
                              key={`${teacher.teacherId}-${key}`}
                              sx={{
                                wordBreak: "break-word",
                                maxWidth: isMobile ? "100px" : "auto",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {key === "phoneNumber" && teacher[key]
                                ? teacher[key].replace(/^91/, "")
                                : key === "specializations"
                                ? renderSpecializations(teacher[key])
                                : teacher[key] || "-"}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </PreviewTable>

              {/* Pagination */}
              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <Button
                  onClick={() =>
                    setPageFetched((prev) => Math.max(prev - 1, 0))
                  }
                  disabled={pageFetched === 0}
                  size={isMobile ? "small" : "medium"}
                >
                  Previous
                </Button>
                <Typography sx={{ mx: 2, alignSelf: "center" }}>
                  Page {pageFetched + 1} of{" "}
                  {Math.ceil(filteredFetchedData.length / rowsPerPage)}
                </Typography>
                <Button
                  onClick={() =>
                    setPageFetched((prev) =>
                      Math.min(
                        prev + 1,
                        Math.ceil(filteredFetchedData.length / rowsPerPage) - 1
                      )
                    )
                  }
                  disabled={
                    pageFetched >=
                    Math.ceil(filteredFetchedData.length / rowsPerPage) - 1
                  }
                  size={isMobile ? "small" : "medium"}
                >
                  Next
                </Button>
              </Box>
            </>
          )}
        </Box>
      )}

      {/* Data Preview Section (for upload tab) */}
      {activeTab === 0 && teacherData.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              justifyContent: "space-between",
              mb: 2,
              gap: isMobile ? 2 : 0,
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold", paddingTop: "50px" }}
            >
              Data Preview ({teacherData.length} records)
            </Typography>

            <TextField
              variant="outlined"
              size="small"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ width: isMobile ? "100%" : 300, paddingTop: "50px" }}
              InputProps={{
                startAdornment: (
                  <SearchIcon sx={{ color: "action.active", mr: 1 }} />
                ),
              }}
            />
          </Box>

          <PreviewTable component={Paper}>
            <Table stickyHeader size={isMobile ? "small" : "medium"}>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  {requiredColumns.map((key) => (
                    <TableCell key={key}>
                      {key.replace(/_/g, " ").toUpperCase()}
                    </TableCell>
                  ))}
                  <TableCell>STATUS</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredData
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((teacher) => (
                    <TableRow
                      key={teacher.id}
                      sx={{
                        backgroundColor: teacher.isValid
                          ? undefined
                          : "rgba(255, 0, 0, 0.1)",
                      }}
                    >
                      <TableCell>{teacher.id}</TableCell>
                      {requiredColumns.map((key) => (
                        <TableCell
                          key={`${teacher.id}-${key}`}
                          sx={{
                            color: teacher.errors?.some((e) => e.includes(key))
                              ? "error.main"
                              : undefined,
                            wordBreak: "break-word",
                            maxWidth: isMobile ? "100px" : "auto",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {key === "phone_number" && teacher[key]
                            ? teacher[key].replace(/^91/, "")
                            : key === "specializations"
                            ? renderSpecializations(teacher[key])
                            : teacher[key] || "-"}
                        </TableCell>
                      ))}
                      <TableCell>
                        {teacher.isValid ? (
                          <Typography color="success.main">Valid</Typography>
                        ) : (
                          <Typography
                            color="error.main"
                            sx={{ fontSize: isMobile ? "0.75rem" : "0.875rem" }}
                          >
                            {renderPhoneNumberError(teacher.errors)}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </PreviewTable>

          {/* Pagination */}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Button
              onClick={() => setPage((prev) => Math.max(prev - 1, 0))}
              disabled={page === 0}
              size={isMobile ? "small" : "medium"}
            >
              Previous
            </Button>
            <Typography sx={{ mx: 2, alignSelf: "center" }}>
              Page {page + 1} of {Math.ceil(filteredData.length / rowsPerPage)}
            </Typography>
            <Button
              onClick={() =>
                setPage((prev) =>
                  Math.min(
                    prev + 1,
                    Math.ceil(filteredData.length / rowsPerPage) - 1
                  )
                )
              }
              disabled={
                page >= Math.ceil(filteredData.length / rowsPerPage) - 1
              }
              size={isMobile ? "small" : "medium"}
            >
              Next
            </Button>
          </Box>
        </Box>
      )}

      <ToastContainer position="bottom-right" autoClose={3000} />
    </Box>
  );
};

export default TeacherDataUpload;
